import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { logout } from '@lootboy/lib/actions/AuthActions';
import { EVerificationCodeField } from '@lootboy/lib/forms/useVerifivationCodeForm';
import { userNeedsEmailVerificationSelector } from '@lootboy/lib/selectors';

import { CodeInput } from '~/components/atoms/FormControls/CodeInput';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import ButtonClean from '~/components/ButtonClean';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';

import {
  INCORRECT_CODE_ERROR,
  useVerifyEmailPopup,
} from '~/popups/VerifyEmailPopup/useVerifyEmailPopup';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import EnsureLoggedInContainer from '~/routes/EnsureLoggedInRoute/EnsureLoggedInContainer';
import { USER_DETAILS_PATH } from '~/routes/paths';
import { useAppHistory } from '~/utilities/routing';

import styles from './VerifyEmailPopup.module.scss';

function getTimerLabel(countdownValue: number): string {
  return `${I18n.t('MenuView_reset_resend_sbutton')} (00:${
    countdownValue < 10 ? `0${countdownValue}` : countdownValue
  })`;
}

const VERIFY_EMAIL_FORM_ID = 'verify_email_form';

export function VerifyEmailPopup(): JSX.Element {
  const history = useAppHistory();
  const dispatch = useAppDispatch();
  const { form, resendCode } = useVerifyEmailPopup();
  const userNeedsEmailVerification = useSelector<TAppStore, boolean>(
    userNeedsEmailVerificationSelector,
  );

  useEffect(() => {
    if (!userNeedsEmailVerification) {
      history.push(USER_DETAILS_PATH);
    }
  }, [history, userNeedsEmailVerification]);

  return (
    <EnsureLoggedInContainer>
      <MenuViewHeader
        title={I18n.t('MenuView_register_bc')}
        onClose={() => {
          dispatch(logout());
        }}
      />
      <section className={styles.container}>
        <AuthViewHeadline
          headline={I18n.t('MenuView_register_code_headline')}
          subHeadline={I18n.t('MenuView_register_code')}
        />
        <form
          className={styles.form}
          onSubmit={form.onSubmit}
          id={VERIFY_EMAIL_FORM_ID}
        >
          <CodeInput
            control={form.control}
            id={EVerificationCodeField.CODE}
            error={form.errors[EVerificationCodeField.CODE]?.message}
            label={I18n.t('MenuView_register_code_enter')}
            name={EVerificationCodeField.CODE}
            errorAction={{
              error: INCORRECT_CODE_ERROR,
              component: (
                <>
                  {I18n.t('MenuView_reset_error_wrong')}
                  <ButtonClean
                    onClick={resendCode.resend}
                    className={styles.resendErrorButton}
                    disabled={resendCode.countdownValue !== 0}
                  >
                    {I18n.t('MenuView_reset_resend_sbutton')}
                  </ButtonClean>
                </>
              ),
            }}
          />
        </form>
      </section>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.GRAY}
          className={styles.resendButton}
          disabled={resendCode.countdownValue !== 0}
          onClick={resendCode.resend}
        >
          {resendCode.countdownValue === 0
            ? I18n.t('MenuView_reset_resend_sbutton')
            : getTimerLabel(resendCode.countdownValue)}
        </StyledButton>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.submitButton}
          type="submit"
          disabled={!form.isSubmitEnabled}
          form={VERIFY_EMAIL_FORM_ID}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </EnsureLoggedInContainer>
  );
}
